// src/pages/RecoveryTrade.js — Distribute recovery bonus by plan percentages
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTransition from "../components/PageTransition";
import toast from "react-hot-toast";
import { giveRecoveryBonus, fetchRecoveryBonusList } from "../services/recoveryTradeApi";
import { useAuth } from "../context/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import {
    FaCoins,
    FaPercentage,
    FaPaperPlane,
    FaHistory,
    FaTimes,
    FaSyncAlt,
    FaChevronLeft,
    FaChevronRight,
    FaCheckCircle,
    FaClock,
} from "react-icons/fa";

const PAGE_SIZE = 20;

const CRYPTO_OPTIONS = [
    { value: "bitcoin", label: "Bitcoin (BTC)" },
    { value: "ethereum", label: "Ethereum (ETH)" },
    { value: "litecoin", label: "Litecoin (LTC)" },
    { value: "ripple", label: "Ripple (XRP)" },
    { value: "solana", label: "Solana (SOL)" },
    { value: "dogecoin", label: "Dogecoin (DOGE)" },
    { value: "bnb", label: "BNB" },
    { value: "cardano", label: "Cardano (ADA)" },
    { value: "tron", label: "Tron (TRX)" },
    { value: "polkadot", label: "Polkadot (DOT)" },
    { value: "chainlink", label: "Chainlink (LINK)" },
    { value: "avalanche", label: "Avalanche (AVAX)" },
    { value: "polygon", label: "Polygon (MATIC)" },
    { value: "shib", label: "Shiba Inu (SHIB)" },
];

const PLAN_FIELDS = [
    { key: "lite", label: "Lite", color: "#6c757d" },
    { key: "plus", label: "Plus", color: "#0d6efd" },
    { key: "pro", label: "Pro", color: "#6f42c1" },
    { key: "max", label: "Max", color: "#fd7e14" },
];

function safeText(v) {
    return v === null || v === undefined || v === "" ? "—" : String(v);
}

function nfmt(v, digits = 0) {
    const num = Number(v);
    if (!Number.isFinite(num)) return safeText(v);
    return new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(num);
}

function formatDateTime(v) {
    if (!v) return "—";
    const normalized = String(v).includes("T") ? v : String(v).replace(" ", "T");
    const dt = new Date(normalized);
    if (!Number.isFinite(dt.getTime())) return safeText(v);
    return dt.toLocaleString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
}

/** Allow digits and at most one decimal point while typing */
function sanitizeDecimalInput(value) {
    const v = String(value ?? "");
    if (!v) return "";
    let cleaned = v.replace(/[^\d.]/g, "");
    const dot = cleaned.indexOf(".");
    if (dot !== -1) {
        cleaned = cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, "");
    }
    return cleaned;
}

function parsePlanPercent(value) {
    const raw = String(value ?? "").trim();
    if (!raw) return { ok: false, message: "Required" };
    if (!/^\d+(\.\d+)?$/.test(raw)) return { ok: false, message: "Enter a valid number" };
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return { ok: false, message: "Must be greater than 0" };
    return { ok: true, value: n };
}

function cryptoLabel(value) {
    return CRYPTO_OPTIONS.find((o) => o.value === value)?.label || safeText(value);
}

function formatMobile(user) {
    const cc = safeText(user?.country_code);
    const mob = safeText(user?.mobile);
    if (cc === "—" && mob === "—") return "—";
    if (cc === "—") return mob;
    if (mob === "—") return cc;
    return `${cc} ${mob}`;
}

function isSessionExpired(out) {
    return typeof out?.error === "string" && out.error === "session expired";
}

function ClaimedBadge({ value }) {
    const claimed = String(value) === "1";
    return (
        <span className={`badge d-inline-flex align-items-center gap-1 ${claimed ? "bg-success" : "bg-warning text-dark"}`}>
            {claimed ? <FaCheckCircle size={10} /> : <FaClock size={10} />}
            {claimed ? "Claimed" : "Pending"}
        </span>
    );
}

function UserCell({ user }) {
    const u = user || {};
    const name = safeText(u?.name);
    const mobile = formatMobile(u);
    const email = safeText(u?.email);
    const hasAny = name !== "—" || mobile !== "—" || email !== "—";

    if (!hasAny) return <span className="text-muted">—</span>;

    return (
        <div className="recovery-user-cell small">
            {name !== "—" ? <div className="fw-semibold">{name}</div> : null}
            {mobile !== "—" ? <div className="text-muted">{mobile}</div> : null}
            {email !== "—" ? <div className="text-muted text-break">{email}</div> : null}
        </div>
    );
}

function PaginationBar({ loading, pageNo, totalPages, onPrev, onNext, onJump }) {
    const pages = useMemo(() => {
        const n = Math.max(1, Number(totalPages) || 1);
        return Array.from({ length: n }, (_, i) => i + 1);
    }, [totalPages]);

    return (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="text-muted small">
                Page <span className="fw-semibold">{pageNo}</span> of{" "}
                <span className="fw-semibold">{totalPages}</span>
            </div>

            <div className="d-flex align-items-center gap-2">
                <button
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onPrev}
                    disabled={loading || pageNo <= 1}
                    type="button"
                >
                    <FaChevronLeft /> Prev
                </button>

                <button
                    className="btn btn-outline-secondary btn-sm px-3 py-1 d-inline-flex align-items-center gap-2"
                    onClick={onNext}
                    disabled={loading || pageNo >= totalPages}
                    type="button"
                >
                    Next <FaChevronRight />
                </button>

                <div className="d-flex align-items-center gap-2 ms-2">
                    <span className="text-muted small">Go to</span>
                    <select
                        className="form-select form-select-sm"
                        value={pageNo}
                        onChange={(e) => onJump(Number(e.target.value))}
                        disabled={loading || totalPages <= 1}
                        style={{ width: "70px", padding: "0.25rem 0.5rem" }}
                    >
                        {pages.map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
}

function ConfirmModal({ open, data, onClose, onConfirm, loading }) {
    return (
        <AnimatePresence>
            {open ? (
                <>
                    <motion.div
                        className="cm-backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        style={{ zIndex: 1050 }}
                    />
                    <motion.div
                        className="cm-wrap"
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                        style={{ zIndex: 1055 }}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="cm-panel" onClick={(e) => e.stopPropagation()}>
                            <div className="cm-head">
                                <div>
                                    <div className="fw-bold">Confirm Recovery Bonus</div>
                                    <div className="text-muted small">Verify crypto and plan percentages before distributing</div>
                                </div>
                                <button className="btn btn-link p-0 cm-x" type="button" onClick={onClose} aria-label="Close" disabled={loading}>
                                    <FaTimes />
                                </button>
                            </div>

                            <div className="cm-body">
                                <div className="border rounded-3 p-3 bg-body-tertiary">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="text-muted small">Crypto</div>
                                        <div className="fw-semibold">{cryptoLabel(data?.crypto)}</div>
                                    </div>
                                    {PLAN_FIELDS.map(({ key, label }) => (
                                        <div key={key} className="d-flex justify-content-between align-items-center mb-2">
                                            <div className="text-muted small">{label}</div>
                                            <div className="fw-semibold">{nfmt(data?.[key], 2)}%</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="text-muted small mt-2">
                                    This will apply recovery bonuses to pending recovery trades for the selected crypto.
                                </div>
                            </div>

                            <div className="cm-foot">
                                <button className="btn btn-outline-secondary" type="button" onClick={onClose} disabled={loading}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary" type="button" onClick={onConfirm} disabled={loading}>
                                    {loading ? "Distributing..." : "Confirm & Distribute"}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            ) : null}
        </AnimatePresence>
    );
}

export default function RecoveryTrade() {
    const { logout } = useAuth();
    const token = useMemo(() => localStorage.getItem("token") || "", []);

    const [crypto, setCrypto] = useState("");
    const [lite, setLite] = useState("");
    const [plus, setPlus] = useState("");
    const [pro, setPro] = useState("");
    const [max, setMax] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [pageNo, setPageNo] = useState(1);
    const [listLoading, setListLoading] = useState(true);
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState({
        total: 0,
        page_no: 1,
        limit: PAGE_SIZE,
        count: 0,
        has_more: false,
        total_page: 1,
    });

    const totalPages = useMemo(() => {
        const apiPages = Number(meta?.total_page || 0);
        if (apiPages > 0) return apiPages;
        const total = Number(meta?.total || 0);
        const limit = Number(meta?.limit || PAGE_SIZE);
        if (!total) return 1;
        return Math.max(1, Math.ceil(total / limit));
    }, [meta?.total_page, meta?.total, meta?.limit]);

    const loadRecords = useCallback(
        async (page) => {
            if (!token) {
                setRecords([]);
                setListLoading(false);
                return;
            }

            setListLoading(true);
            try {
                const out = await fetchRecoveryBonusList({
                    token,
                    page_no: page,
                    limit: PAGE_SIZE,
                });

                if (isSessionExpired(out)) {
                    toast.error("Session expired. Please login again.");
                    logout();
                    return;
                }

                if (out?.error) {
                    toast.error(typeof out.error === "string" ? out.error : "Failed to load recovery bonus records");
                    setRecords([]);
                    setMeta({ total: 0, page_no: page, limit: PAGE_SIZE, count: 0, has_more: false, total_page: 1 });
                    return;
                }

                const list = Array.isArray(out?.data) ? out.data : [];
                const m = out?.meta || {};
                setRecords(list);
                setMeta({
                    total: Number(m?.total || 0),
                    page_no: Number(m?.page_no || page),
                    limit: Number(m?.limit || PAGE_SIZE),
                    count: Number(m?.count || list.length),
                    has_more: !!m?.has_more,
                    total_page: Math.max(1, Number(m?.total_page || 1)),
                });
            } catch (err) {
                console.error(err);
                toast.error("Failed to load recovery bonus records");
                setRecords([]);
                setMeta({ total: 0, page_no: page, limit: PAGE_SIZE, count: 0, has_more: false, total_page: 1 });
            } finally {
                setListLoading(false);
            }
        },
        [token, logout]
    );

    useEffect(() => {
        loadRecords(pageNo);
    }, [pageNo, loadRecords]);

    const onPrev = () => setPageNo((p) => Math.max(1, p - 1));
    const onNext = () => setPageNo((p) => Math.min(totalPages, p + 1));
    const onJump = (p) => setPageNo(Math.min(totalPages, Math.max(1, p)));
    const onRefreshList = () => loadRecords(pageNo);

    const validateForm = useCallback(() => {
        const cryptoVal = crypto.trim();
        if (!cryptoVal) {
            toast.error("Crypto is required");
            return null;
        }

        const parsed = {};
        for (const { key, label } of PLAN_FIELDS) {
            const state = { lite, plus, pro, max }[key];
            const check = parsePlanPercent(state);
            if (!check.ok) {
                toast.error(`${label}: ${check.message}`);
                return null;
            }
            parsed[key] = check.value;
        }

        return {
            crypto: cryptoVal,
            lite: parsed.lite,
            plus: parsed.plus,
            pro: parsed.pro,
            max: parsed.max,
        };
    }, [crypto, lite, plus, pro, max]);

    const confirmData = useMemo(
        () => ({
            crypto: crypto.trim(),
            lite,
            plus,
            pro,
            max,
        }),
        [crypto, lite, plus, pro, max]
    );

    const handleDistributeResponse = useCallback(
        async (out) => {
            if (out?.error) {
                const err = typeof out.error === "string" ? out.error : "Request failed";
                if (err === "session expired") {
                    toast.error("Session expired. Please login again.");
                    logout();
                    return;
                }
                toast.error(err);
                return;
            }

            const data = out?.data || {};
            const processed = Number(data?.processed) || 0;

            if (processed === 0) {
                toast(out?.msg || "No recovery trades with pending amount", { icon: "ℹ️" });
            } else {
                toast.success(out?.msg || "Recovery bonus distributed successfully");
            }

            if (pageNo !== 1) {
                setPageNo(1);
            } else {
                await loadRecords(1);
            }
        },
        [logout, loadRecords, pageNo]
    );

    const openConfirm = (e) => {
        e.preventDefault();
        const payload = validateForm();
        if (!payload) return;
        setConfirmOpen(true);
    };

    const closeConfirm = () => {
        if (submitting) return;
        setConfirmOpen(false);
    };

    const submitDistribution = async () => {
        const payload = validateForm();
        if (!payload) return;

        if (!token) {
            toast.error("Session missing. Please login again.");
            logout();
            return;
        }

        setSubmitting(true);
        try {
            const out = await giveRecoveryBonus({ token, ...payload });
            await handleDistributeResponse(out);
            setConfirmOpen(false);
        } catch (err) {
            console.error(err);
            toast.error("Failed to distribute recovery bonus");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageTransition>
            <style>{`
                .recovery-hero {
                    border-radius: 14px;
                    background: linear-gradient(135deg, rgba(13,110,253,.08), rgba(111,66,193,.06));
                    border: 1px solid rgba(15,23,42,.08);
                }
                .plan-field-card {
                    border-radius: 12px;
                    border: 1px solid rgba(0,0,0,.08);
                    background: #fff;
                    height: 100%;
                }
                .plan-field-head {
                    font-size: 0.75rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    padding: 8px 12px;
                    border-bottom: 1px solid rgba(0,0,0,.06);
                    color: #fff;
                    border-radius: 12px 12px 0 0;
                }
                .tight th, .tight td { padding: 10px 12px !important; vertical-align: middle; }
                .recovery-records-table th {
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.04em;
                    white-space: nowrap;
                }
                .cm-backdrop {
                    position: fixed; inset: 0;
                    background: rgba(0,0,0,0.58);
                    backdrop-filter: blur(2px);
                }
                .cm-wrap {
                    position: fixed; inset: 0;
                    display: flex; align-items: center; justify-content: center;
                    padding: 16px;
                }
                .cm-panel {
                    width: 100%;
                    max-width: 520px;
                    background: #fff;
                    border-radius: 14px;
                    overflow: hidden;
                    box-shadow: 0 18px 45px rgba(0,0,0,0.28);
                    border: 1px solid rgba(255,255,255,0.10);
                }
                .cm-head {
                    padding: 14px 16px;
                    display: flex; align-items: flex-start; justify-content: space-between;
                    border-bottom: 1px solid rgba(0,0,0,0.06);
                }
                .cm-x { color: rgba(0,0,0,0.55); }
                .cm-x:hover { color: rgba(0,0,0,0.85); }
                .cm-body { padding: 14px 16px; }
                .cm-foot {
                    padding: 12px 16px;
                    display: flex; justify-content: flex-end; gap: 10px;
                    border-top: 1px solid rgba(0,0,0,0.06);
                    background: #fafafa;
                }
            `}</style>

            <div className="container-fluid py-3">
                <div className="recovery-hero p-4 mb-4">
                    <h1 className="h3 fw-bold mb-0 d-flex align-items-center gap-2">
                        <FaCoins className="text-primary" />
                        Recovery Trade Bonus
                    </h1>
                </div>

                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 14 }}>
                    <div className="card-body p-4">
                        <form onSubmit={openConfirm} noValidate>
                            <div className="mb-4">
                                <label className="form-label fw-semibold small text-muted">
                                    Crypto <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-select form-select-lg"
                                    value={crypto}
                                    onChange={(e) => setCrypto(e.target.value)}
                                    disabled={submitting}
                                    required
                                >
                                    <option value="">Select cryptocurrency</option>
                                    {CRYPTO_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-2">
                                <div className="fw-semibold small text-muted mb-3 d-flex align-items-center gap-2">
                                    <FaPercentage className="text-primary" />
                                    Plan recovery percentages <span className="text-danger">*</span>
                                </div>
                                <div className="row g-3">
                                    {PLAN_FIELDS.map(({ key, label, color }) => (
                                        <div key={key} className="col-6 col-md-3">
                                            <div className="plan-field-card">
                                                <div className="plan-field-head" style={{ background: color }}>
                                                    {label}
                                                </div>
                                                <div className="p-3">
                                                    <div className="input-group input-group-sm">
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            autoComplete="off"
                                                            className="form-control text-center recovery-plan-input"
                                                            placeholder="e.g. 2"
                                                            value={{ lite, plus, pro, max }[key]}
                                                            onChange={(e) => {
                                                                const v = sanitizeDecimalInput(e.target.value);
                                                                if (key === "lite") setLite(v);
                                                                if (key === "plus") setPlus(v);
                                                                if (key === "pro") setPro(v);
                                                                if (key === "max") setMax(v);
                                                            }}
                                                            disabled={submitting}
                                                            required
                                                        />
                                                        <span className="input-group-text">%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="d-flex justify-content-md-end mt-4">
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2 px-3"
                                    disabled={submitting}
                                >
                                    <FaPaperPlane />
                                    Distribute
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
                    <div className="card-body p-0">
                        <div className="px-4 py-3 border-bottom bg-light d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <div className="d-flex align-items-center gap-2">
                                <FaHistory className="text-primary" />
                                <h2 className="h6 fw-bold mb-0">Recovery bonus records</h2>
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
                                onClick={onRefreshList}
                                disabled={listLoading}
                            >
                                <FaSyncAlt className={listLoading ? "tb-spin" : ""} />
                                {listLoading ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>

                        <div className="px-3 py-3 border-bottom bg-light">
                            <PaginationBar
                                loading={listLoading}
                                pageNo={pageNo}
                                totalPages={totalPages}
                                onPrev={onPrev}
                                onNext={onNext}
                                onJump={onJump}
                            />
                        </div>

                        <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0 tight table-hover recovery-records-table">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "8%" }}>S.No</th>
                                        <th style={{ minWidth: 160 }}>Date</th>
                                        <th style={{ minWidth: 200 }}>User</th>
                                        <th>Crypto</th>
                                        <th className="text-end">%</th>
                                        <th className="text-end">Amount</th>
                                        <th>Claimed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listLoading ? (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-5">
                                                <span className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                                Loading records…
                                            </td>
                                        </tr>
                                    ) : records.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-5">
                                                No recovery bonus records found.
                                            </td>
                                        </tr>
                                    ) : (
                                        records.map((r, idx) => (
                                            <tr key={`${r?.create_date}-${idx}`}>
                                                <td className="fw-semibold">
                                                    {(pageNo - 1) * PAGE_SIZE + idx + 1}
                                                </td>
                                                <td className="small">{formatDateTime(r?.create_date)}</td>
                                                <td>
                                                    <UserCell user={r?.user} />
                                                </td>
                                                <td>
                                                    <span className="badge bg-light text-dark border">{safeText(r?.crypto)}</span>
                                                </td>
                                                <td className="text-end">{nfmt(r?.percentage, 2)}%</td>
                                                <td className="text-end fw-semibold">{nfmt(r?.amount, 2)}</td>
                                                <td>
                                                    <ClaimedBadge value={r?.is_claimed} />
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="border-top p-3 bg-light">
                            <PaginationBar
                                loading={listLoading}
                                pageNo={pageNo}
                                totalPages={totalPages}
                                onPrev={onPrev}
                                onNext={onNext}
                                onJump={onJump}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmModal
                open={confirmOpen}
                data={confirmData}
                onClose={closeConfirm}
                onConfirm={submitDistribution}
                loading={submitting}
            />

        </PageTransition>
    );
}
